import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  Table,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  message,
  Tooltip,
  Popconfirm,
  Tag,
  Tabs,
  Spin,
  Select,
  Alert,

} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  SearchOutlined,
  HistoryOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { Editor } from '@monaco-editor/react';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { useTemplatesList } from '../hooks/useAppsQueries';
import { Template } from '../types/api';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { ValidationErrorsCard } from '../components/ValidationErrorsCard';

const { Title, Text } = Typography;
const { TextArea } = Input;

export const Templates: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  
  // Визначаємо режим на основі URL
  const isCreateMode = location.pathname.includes('/create');
  const isEditMode = location.pathname.includes('/edit') && !!id;
  const isFormMode = isCreateMode || isEditMode;
  
  const queryClient = useQueryClient();
  const templatesQuery = useTemplatesList();
  const templates = templatesQuery.data ?? [];
  const loading = templatesQuery.isLoading;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [codeValue, setCodeValue] = useState('');
  const [isEditorLoading, setIsEditorLoading] = useState(true);
  
  // Відстеження змін
  const [originalValues, setOriginalValues] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isVersionHistoryVisible, setIsVersionHistoryVisible] = useState(false);
  const [versionHistory, setVersionHistory] = useState<any>(null);
  
  // Копіювання шаблону
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);
  const [copyVersions, setCopyVersions] = useState<any[]>([]);
  const [copyForm] = Form.useForm();
  
  // Валідація коду
  const [codeValidation, setCodeValidation] = useState<{
    isValid: boolean;
    errors: string[];
    detailedErrors?: Array<{
      message: string;
      line?: number;
      column?: number;
      type: 'syntax' | 'macro' | 'macro-comment' | 'security';
    }>;
    exports: Array<{name: string; description?: string}>;
    endpoints: Array<{path: string; method: string; handler: string; description?: string; required: boolean}>;
    macros: Array<{name: string; type: string; description?: string; defaultValue?: string}>;
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  
  const { t } = useTranslation();
  const { monacoTheme } = useTheme();

  // Ініціалізація форми на основі режиму
  useEffect(() => {
    if (isCreateMode) {
      setEditingTemplate(null);
      const defaultCode = `// Основний обробник для вебхуків
export async function handleWebhook(request, env) {
  try {
    const body = await request.json();
    
    // Використовуй макроси для конфігурації
    const affiliateId = "{{AFFILIATE_ID}}";
    const apiKey = "{{API_KEY}}";
    
    // Твоя логіка тут
    if (body.event === 'conversion') {
      return new Response(JSON.stringify({
        status: 'success',
        redirect_url: "{{OFFER_URL}}"
      }), {
        headers: { 'Content-Type': 'application/json' }
      });
    }
    
    return new Response(JSON.stringify({
      status: 'error',
      error_url: "{{OFFER_ERROR}}"
    }), {
      status: 400,
      headers: { 'Content-Type': 'application/json' }
    });
    
  } catch (error) {
    return new Response(JSON.stringify({
      status: 'error',
      message: error.message,
      error_url: "{{OFFER_ERROR}}"
    }), {
      status: 500,
      headers: { 'Content-Type': 'application/json' }
    });
  }
}`;
      const defaultValues = {
        name: '',
        description: '',
        category: 'api',
        code: defaultCode,
      };
      
      form.resetFields();
      form.setFieldsValue(defaultValues);
      setCodeValue(defaultCode);
      setOriginalValues(defaultValues);
      setHasChanges(false);
      setIsEditorLoading(true);
    } else if (isEditMode && id) {
      const template = templates.find(t => t.id === id);
      if (template) {
        setEditingTemplate(template);
        
        const originalTemplateValues = {
          name: template.name,
          description: template.description,
          category: template.category || 'api',
          code: template.code,
        };
        
        form.setFieldsValue(originalTemplateValues);
        setCodeValue(template.code || '');
        setOriginalValues(originalTemplateValues);
        setHasChanges(false);
        setIsEditorLoading(true);
      }
    }
  }, [isCreateMode, isEditMode, id, templates]);

  // Валідація коду з дебаунсом
  useEffect(() => {
    if (!codeValue || codeValue.trim().length === 0) {
      setCodeValidation(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsValidating(true);
        const validation = await apiService.validateTemplateCode(codeValue);
        setCodeValidation(validation);
      } catch (error: any) {
        console.error('Error validating code:', error);
        let errorMessage = 'Помилка з\'єднання з сервером';
        
        if (error?.response?.status === 401) {
          errorMessage = 'Помилка авторизації. Перезайдіть в систему.';
        } else if (error?.response?.status === 400) {
          errorMessage = 'Невірний формат коду';
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        setCodeValidation({
          isValid: false,
          errors: [errorMessage],
          detailedErrors: [{
            message: errorMessage,
            type: 'syntax'
          }],
          exports: [],
          endpoints: [],
          macros: [] as Array<{name: string; type: string; description?: string; defaultValue?: string}>
        });
      } finally {
        setIsValidating(false);
      }
    }, 1000); // 1 секунда дебаунс

    return () => clearTimeout(timeoutId);
  }, [codeValue]);

  // Функція для перевірки змін
  const checkForChanges = (currentValues: any) => {
    if (!originalValues) {
      setHasChanges(false);
      return;
    }

    const hasNameChange = currentValues.name !== originalValues.name;
    const hasDescriptionChange = currentValues.description !== originalValues.description;
    const hasCategoryChange = currentValues.category !== originalValues.category;
    const hasVersionChange = currentValues.version !== originalValues.version;
    const hasCodeChange = currentValues.code !== originalValues.code;

    const hasAnyChanges = hasNameChange || hasDescriptionChange || hasCategoryChange || hasVersionChange || hasCodeChange;
    setHasChanges(hasAnyChanges);
  };

  // Відстеження змін в формі
  useEffect(() => {
    const subscription = form.getFieldsValue();
    if (originalValues) {
      checkForChanges({ ...subscription, code: codeValue });
    }
  }, [originalValues, codeValue]);

  // Відстеження змін в полях форми
  const handleFormChange = () => {
    const currentValues = form.getFieldsValue();
    checkForChanges({ ...currentValues, code: codeValue });
  };

  const handleCreateTemplate = () => {
    navigate('/templates/create');
  };

  const handleEditTemplate = (template: Template) => {
    navigate(`/templates/edit/${template.id}`);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await apiService.deleteTemplate(templateId);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      message.success('Шаблон видалено');
    } catch (error) {
      console.error('Помилка видалення шаблону:', error);
      message.error(`Не вдалося видалити шаблон: ${error instanceof Error ? error.message : 'Невідома помилка'}`);
    }
  };

  const handleCloneTemplate = async (template: Template) => {
    try {
      // Завантажуємо версії шаблону
      const versions = await apiService.getTemplateVersions(template.id);
      setCopyVersions(versions);
      
      // Встановлюємо дефолтні значення форми
      copyForm.setFieldsValue({
        name: `${template.name} (копія)`,
        description: template.description,
        category: template.category || 'api',
        selectedVersion: versions.find((v: any) => v.isActive)?.id || versions[0]?.id
      });
      
      setIsCopyModalVisible(true);
    } catch (error) {
      console.error('Помилка завантаження версій шаблону:', error);
      message.error('Не вдалося завантажити версії шаблону');
    }
  };

  const handleConfirmCopy = async (values: any) => {
    try {
      // Перевіряємо унікальність назви
      const existingTemplate = templates.find(t => t.name.toLowerCase() === values.name.toLowerCase());
      if (existingTemplate) {
        message.error('Шаблон з такою назвою вже існує');
        return;
      }

      // Знаходимо обрану версію
      const selectedVersion = copyVersions.find(v => v.id === values.selectedVersion);
      if (!selectedVersion) {
        message.error('Не знайдено обрану версію');
        return;
      }

      const clonedTemplateData = {
        name: values.name,
        description: values.description,
        category: values.category,
        code: selectedVersion.sourceCode,
      };
      
      await apiService.createTemplate(clonedTemplateData);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      message.success('Шаблон скопійовано');
      
      setIsCopyModalVisible(false);
      copyForm.resetFields();
      setCopyVersions([]);
    } catch (error: any) {
      console.error('Помилка копіювання шаблону:', error);
      if (error?.response?.status === 409) {
        message.error('Шаблон з такою назвою вже існує');
      } else {
        message.error(`Не вдалося скопіювати шаблон: ${error instanceof Error ? error.message : 'Невідома помилка'}`);
      }
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Перевіряємо валідність коду перед відправкою
      if (!codeValidation?.isValid) {
        message.error('Код містить помилки. Виправте їх перед збереженням.');
        return;
      }

      const templateData = {
        name: values.name,
        description: values.description,
        category: values.category,
        code: values.code,
      };

    if (editingTemplate) {
        await apiService.updateTemplate(editingTemplate.id, templateData);
      message.success('Створено нову версію шаблону');
    } else {
        await apiService.createTemplate(templateData);
      message.success('Шаблон створено');
    }

    navigate('/templates');
    setHasChanges(false);
    await queryClient.invalidateQueries({ queryKey: ['templates'] });
    } catch (error: any) {
      console.error('Помилка збереження шаблону:', error);
      if (error?.response?.status === 409) {
        message.error('Шаблон з такою назвою вже існує');
      } else {
        message.error(`${t('templates.loadError')}: ${error instanceof Error ? error.message : t('common.error')}`);
      }
    }
  };

  const handleViewVersionHistory = async (template: Template) => {
    try {
      const versions = await apiService.getTemplateVersions(template.id);
      setVersionHistory({ templateId: template.id, versions: versions });
      setIsVersionHistoryVisible(true);
    } catch (error) {
      console.error('Помилка завантаження історії версій:', error);
      message.error('Не вдалося завантажити історію версій');
    }
  };

  const handleRollbackToVersion = async (templateId: string, versionId: string) => {
    try {
      await apiService.rollbackTemplateVersion(templateId, versionId);
      message.success(`Відкачено до обраної версії`);
      setIsVersionHistoryVisible(false);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
    } catch (error) {
      console.error('Помилка відкату:', error);
      message.error('Не вдалося відкатити до обраної версії');
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (template.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const columns = [
    {
      title: t('ui.table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Template) => (
        <div>
          <div>
            <Text strong>{text}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => {
        const isWebSocket = (category || '').toLowerCase() === 'websocket';
        return (
          <Tag color={isWebSocket ? 'blue' : 'default'}>
            {isWebSocket ? 'Web-Socket' : (category ? category.toUpperCase() : 'API')}
          </Tag>
        );
      },
    },
    {
      title: t('ui.table.version'),
      dataIndex: 'currentVersion',
      key: 'currentVersion',
      width: 120,
              render: (currentVersion: string, record: Template) => (
        <div>
          <div>
            <Text strong>Версія {currentVersion || '1'}</Text>
            <Tag color="green" style={{ marginLeft: 8 }}>Активна</Tag>
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Оновлено: {new Date(record.updatedAt || '').toLocaleDateString('uk-UA')}
          </Text>
        </div>
      ),
    },

    {
      title: t('ui.table.actions'),
      key: 'actions',
      width: 160,
      render: (_: any, record: Template) => (
        <Space>
          <Tooltip title="Редагувати">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditTemplate(record)}
            />
          </Tooltip>
          <Tooltip title="Історія версій">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => handleViewVersionHistory(record)}
            />
          </Tooltip>
          <Tooltip title="Копіювати">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleCloneTemplate(record)}
            />
          </Tooltip>
          <Tooltip title="Видалити">
            <Popconfirm
              title={t('templates.deleteConfirm')}
              onConfirm={() => handleDeleteTemplate(record.id)}
              okText={t('templates.yes')}
              cancelText={t('templates.no')}
            >
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Якщо це режим створення/редагування - показуємо форму
  if (isFormMode) {
    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/templates')}
                type="text"
              />
              {isCreateMode ? t('templates.create') : t('templates.edit')}
            </Space>
          </Title>
        </div>

        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleFormChange}
          >
            <Tabs defaultActiveKey="basic" items={[
              {
                key: 'basic',
                label: t('templates.basicInfo'),
                children: (
                  <div>
                    <Form.Item
                      label={t('templates.templateName')}
                      name="name"
                      rules={[{ required: true, message: t('templates.nameRequired') }]}
                    >
                      <Input placeholder={t('templates.namePlaceholder')} />
                    </Form.Item>

                    <Form.Item
                      label={t('templates.templateDescription')}
                      name="description"
                    >
                      <Input.TextArea rows={3} placeholder={t('templates.descriptionPlaceholder')} />
                    </Form.Item>

                    <Form.Item
                      label="Category"
                      name="category"
                      rules={[{ required: true, message: 'Оберіть категорію шаблону' }]}
                    >
                      <Select placeholder="Оберіть категорію">
                        <Select.Option value="api">GET API</Select.Option>
                        <Select.Option value="websocket">Web-Socket</Select.Option>
                      </Select>
                    </Form.Item>
                  </div>
                ),
              },
              {
                key: 'code',
                label: t('templates.templateCode'),
                children: (
                  <div>
                    <Form.Item
                      label={t('templates.templateCode')}
                      name="code"
                      rules={[{ required: true, message: t('templates.codeRequired') }]}
                    >
                      <Spin spinning={isEditorLoading} tip={t('templates.editorLoading')}>
                        <Editor
                          height="500px"
                          language="javascript"
                          theme={monacoTheme}
                          value={codeValue}
                          onChange={(value) => {
                            setCodeValue(value || '');
                            form.setFieldsValue({ code: value });
                            const currentValues = form.getFieldsValue();
                            checkForChanges({ ...currentValues, code: value });
                          }}
                          onMount={() => setIsEditorLoading(false)}
                          options={{
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            fontSize: 12,
                            lineNumbers: 'on',
                            automaticLayout: true,
                            renderLineHighlight: 'none',
                          }}
                        />
                      </Spin>
                    </Form.Item>

                    <div style={{ marginTop: 16 }}>
                      <Typography.Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
                        💡 Використовуйте макроси в форматі {`{{MACRO_NAME}}`} для динамічних значень.
                        Всі знайдені макроси будуть доступні на вкладці "Аналіз коду".
                      </Typography.Text>
                    </div>
                  </div>
                ),
              },
              {
                key: 'analysis',
                label: 'Аналіз коду',
                children: (
                  <div>
                    {isValidating && (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin /> <span style={{ marginLeft: '8px' }}>Аналіз коду...</span>
                      </div>
                    )}
                    
                    {codeValidation && !isValidating && (
                      <Space direction="vertical" style={{ width: '100%' }} size="large">
                        {/* Помилки */}
                        {codeValidation && !codeValidation.isValid && (
                          <ValidationErrorsCard
                            detailedErrors={codeValidation.detailedErrors}
                            errors={codeValidation.errors}
                          />
                        )}
                        
                        {/* Успішна валідація */}
                        {codeValidation && codeValidation.isValid && (
                          <Card title="✅ Код валідний" type="inner" style={{ borderColor: '#52c41a' }}>
                            <Typography.Text type="success">Код шаблону не містить помилок</Typography.Text>
                          </Card>
                        )}

                        {/* Експортні функції */}
                        {codeValidation && codeValidation.exports && codeValidation.exports.length > 0 && (
                          <Card title="📤 Знайдені експортні функції" type="inner">
                            {codeValidation.exports?.map((exp, index) => (
                              <div key={index} style={{ marginBottom: '8px' }}>
                                <Typography.Text code>{exp.name}</Typography.Text>
                                {exp.description && <Typography.Text type="secondary"> - {exp.description}</Typography.Text>}
                              </div>
                            ))}
                          </Card>
                        )}

                        {/* Ендпоінти */}
                        {codeValidation && codeValidation.endpoints && codeValidation.endpoints.length > 0 && (
                          <Card title="🔗 Згенеровані ендпоінти" type="inner">
                            {codeValidation.endpoints?.map((endpoint, index) => (
                              <div key={index} style={{ marginBottom: '8px' }}>
                                <Tag color={endpoint.method === 'GET' ? 'green' : endpoint.method === 'POST' ? 'blue' : 'orange'}>
                                  {endpoint.method}
                                </Tag>
                                <Typography.Text code>{endpoint.path}</Typography.Text>
                                <Typography.Text type="secondary"> → {endpoint.handler}</Typography.Text>
                                {endpoint.description && <div style={{ marginLeft: '20px', fontSize: '12px', color: '#666' }}>{endpoint.description}</div>}
                              </div>
                            ))}
                          </Card>
                        )}

                        {/* Макроси */}
                        {codeValidation && codeValidation.macros && codeValidation.macros.length > 0 && (
                          <Card title="📋 Знайдені макроси" type="inner">
                            <div>
                              {codeValidation.macros?.map((macro, index) => (
                                <div key={index} style={{ marginBottom: '8px' }}>
                                  <Tag color={macro.type === 'encryption' ? 'green' : macro.type === 'url' ? 'blue' : 'default'} style={{ marginRight: '8px' }}>
                                    {`{{${macro.name}}}`}
                                  </Tag>
                                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                    {macro.type} {macro.description && `- ${macro.description}`}
                                    {macro.defaultValue && <span style={{ color: '#1890ff' }}> (за замовчуванням: {macro.defaultValue})</span>}
                                  </Typography.Text>
                                </div>
                              ))}
                            </div>
                            <div style={{ marginTop: '12px', color: '#666', fontSize: '12px' }}>
                              💡 Системні макроси (OFFER_URL, OFFER_ERROR) налаштовуються в роутингу, інші стають динамічними полями
                            </div>
                          </Card>
                        )}
                      </Space>
                    )}

                    {!codeValidation && !isValidating && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        Напишіть код шаблону в редакторі для автоматичного аналізу
                      </div>
                    )}
                  </div>
                ),
              },
            ]} />

            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <Button onClick={() => navigate('/templates')}>
                  {t('templates.cancel')}
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  disabled={!hasChanges}
                  title={!hasChanges ? (editingTemplate ? 'Немає змін для збереження' : 'Заповніть всі поля') : ''}
                >
                  {editingTemplate ? `${t('templates.save')} (нова версія)` : t('templates.save')}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </div>
    );
  }

  // Якщо це список - показуємо таблицю
  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>{t('templates.title')}</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateTemplate}
        >
          {t('templates.create')}
        </Button>
      </div>

      <Card>
        <Input
          placeholder={t('templates.search')}
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        
        <Table
          dataSource={filteredTemplates}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          loading={loading}
        />
      </Card>

      <Modal
        title={editingTemplate ? t('templates.edit') : t('templates.create')}
        open={isModalVisible && !isFormMode}
        onCancel={() => navigate('/templates')}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
        >
          <Tabs defaultActiveKey="basic" items={[
            {
              key: 'basic',
              label: t('templates.basicInfo'),
              children: (
                <div>
                  <Form.Item
                    label={t('templates.templateName')}
                    name="name"
                    rules={[{ required: true, message: t('templates.nameRequired') }]}
                  >
                    <Input placeholder={t('templates.namePlaceholder')} />
                  </Form.Item>



                  <Form.Item
                    label={t('templates.templateDescription')}
                    name="description"
                  >
                    <TextArea rows={3} placeholder={t('templates.descriptionPlaceholder')} />
                  </Form.Item>
                </div>
              ),
            },
            {
              key: 'code',
              label: t('templates.templateCode'),
              children: (
                <div>
                  <Form.Item
                    label={t('templates.templateCode')}
                    name="code"
                    rules={[{ required: true, message: t('templates.codeRequired') }]}
                  >
                    <Spin spinning={isEditorLoading} tip={t('templates.editorLoading')}>
                      <Editor
                        height="500px"
                        language="javascript"
                        theme={monacoTheme}
                        value={codeValue}
                        onChange={(value) => {
                          setCodeValue(value || '');
                          form.setFieldsValue({ code: value });
                          const currentValues = form.getFieldsValue();
                          checkForChanges({ ...currentValues, code: value });
                        }}
                        onMount={() => setIsEditorLoading(false)}
                        options={{
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 12,
                          lineNumbers: 'on',
                          automaticLayout: true,
                          renderLineHighlight: 'none',
                        }}
                      />
                    </Spin>
                  </Form.Item>

                  <div style={{ marginTop: 16 }}>
                    <Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
                      💡 Використовуйте макроси в форматі {`{{MACRO_NAME}}`} для динамічних значень.
                      Всі знайдені макроси будуть доступні на вкладці "Аналіз коду".
                    </Text>
                  </div>
                </div>
              ),
            },
            {
              key: 'analysis',
              label: 'Аналіз коду',
              children: (
                <div>
                  {isValidating && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Spin /> <span style={{ marginLeft: '8px' }}>Аналіз коду...</span>
                    </div>
                  )}
                  
                  {codeValidation && !isValidating && (
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                      {/* Помилки */}
                      {codeValidation && !codeValidation.isValid && (
                        <ValidationErrorsCard
                          detailedErrors={codeValidation.detailedErrors}
                          errors={codeValidation.errors}
                        />
                      )}
                      
                      {/* Успішна валідація */}
                      {codeValidation && codeValidation.isValid && (
                        <Card title="✅ Код валідний" type="inner" style={{ borderColor: '#52c41a' }}>
                          <Text type="success">Код шаблону не містить помилок</Text>
                        </Card>
                      )}

                      {/* Експортні функції */}
                      {codeValidation && codeValidation.exports && codeValidation.exports.length > 0 && (
                        <Card title="📤 Знайдені експортні функції" type="inner">
                          {codeValidation.exports?.map((exp, index) => (
                            <div key={index} style={{ marginBottom: '8px' }}>
                              <Text code>{exp.name}</Text>
                              {exp.description && <Text type="secondary"> - {exp.description}</Text>}
                            </div>
                          ))}
                        </Card>
                      )}

                      {/* Ендпоінти */}
                      {codeValidation && codeValidation.endpoints && codeValidation.endpoints.length > 0 && (
                        <Card title="🔗 Згенеровані ендпоінти" type="inner">
                          {codeValidation.endpoints?.map((endpoint, index) => (
                            <div key={index} style={{ marginBottom: '8px' }}>
                              <Tag color={endpoint.method === 'GET' ? 'green' : endpoint.method === 'POST' ? 'blue' : 'orange'}>
                                {endpoint.method}
                              </Tag>
                              <Text code>{endpoint.path}</Text>
                              <Text type="secondary"> → {endpoint.handler}</Text>
                              {endpoint.description && <div style={{ marginLeft: '20px', fontSize: '12px', color: '#666' }}>{endpoint.description}</div>}
                            </div>
                          ))}
                        </Card>
                      )}

                      {/* Макроси */}
                      {codeValidation && codeValidation.macros && codeValidation.macros.length > 0 && (
                        <Card title="📋 Знайдені макроси" type="inner">
                          <div>
                            {codeValidation.macros?.map((macro, index) => (
                              <div key={index} style={{ marginBottom: '8px' }}>
                                <Tag color={macro.type === 'encryption' ? 'green' : macro.type === 'url' ? 'blue' : 'default'} style={{ marginRight: '8px' }}>
                                  {`{{${macro.name}}}`}
                                </Tag>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {macro.type} {macro.description && `- ${macro.description}`}
                                  {macro.defaultValue && <span style={{ color: '#1890ff' }}> (за замовчуванням: {macro.defaultValue})</span>}
                                </Text>
                              </div>
                            ))}
                          </div>
                          <div style={{ marginTop: '12px', color: '#666', fontSize: '12px' }}>
                            💡 Системні макроси (OFFER_URL, OFFER_ERROR) налаштовуються в роутингу, інші стають динамічними полями
                          </div>
                        </Card>
                      )}
                    </Space>
                  )}

                  {!codeValidation && !isValidating && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      Напишіть код шаблону в редакторі для автоматичного аналізу
                    </div>
                  )}
                </div>
              ),
            },
          ]} />

          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                {t('templates.cancel')}
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                disabled={!hasChanges}
                title={!hasChanges ? (editingTemplate ? 'Немає змін для збереження' : 'Заповніть всі поля') : ''}
              >
                {editingTemplate ? `${t('templates.save')} (нова версія)` : t('templates.save')}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Модальне вікно історії версій */}
      <Modal
        title="Історія версій шаблону"
        open={isVersionHistoryVisible}
        onCancel={() => setIsVersionHistoryVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsVersionHistoryVisible(false)}>
            Закрити
          </Button>
        ]}
        width={800}
      >
        <Table
          dataSource={versionHistory?.versions || []}
          pagination={false}
          size="small"
          rowKey={(record) => record.id}
          columns={[
            {
              title: 'Версія',
              dataIndex: 'version',
              key: 'version',
              width: 100,
              render: (version: string, record: any) => (
                <div>
                  <Text strong={record.isActive}>Версія {version}</Text>
                  {record.isActive && <Tag color="green" style={{ marginLeft: 8 }}>Активна</Tag>}
                </div>
              ),
            },
            {
              title: 'Коментар',
              dataIndex: 'comment',
              key: 'comment',
            },
            {
              title: 'Дата створення',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 150,
              render: (date: string) => new Date(date).toLocaleString('uk-UA'),
            },
            {
              title: 'Дії',
              key: 'actions',
              width: 100,
              render: (_: any, record: any) => (
                !record.isActive ? (
                  <Popconfirm
                    title={`Відкатити до версії ${record.version}?`}
                    description="Це встановить обрану версію як поточну активну версію шаблону"
                    onConfirm={() => handleRollbackToVersion(versionHistory?.templateId, record.id)}
                    okText="Так"
                    cancelText="Ні"
                  >
                    <Button size="small" type="primary">
                      Відкатити
                    </Button>
                  </Popconfirm>
                ) : (
                  <Text type="secondary">Активна</Text>
                )
              ),
            },
          ]}
        />
      </Modal>

      {/* Модальне вікно копіювання шаблону */}
      <Modal
        title="Копіювання шаблону"
        open={isCopyModalVisible}
        onCancel={() => {
          setIsCopyModalVisible(false);
          copyForm.resetFields();
          setCopyVersions([]);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={copyForm}
          layout="vertical"
          onFinish={handleConfirmCopy}
        >
          <Form.Item
            label="Назва нового шаблону"
            name="name"
            rules={[
              { required: true, message: 'Введіть назву шаблону' },
              { min: 3, message: 'Назва має містити мінімум 3 символи' }
            ]}
          >
            <Input placeholder="Введіть назву нового шаблону" />
          </Form.Item>

          <Form.Item
            label="Опис"
            name="description"
          >
            <Input.TextArea 
              placeholder="Введіть опис шаблону" 
              rows={3}
            />
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: 'Оберіть категорію шаблону' }]}
          >
            <Select placeholder="Оберіть категорію">
              <Select.Option value="api">GET API</Select.Option>
              <Select.Option value="websocket">Web-Socket</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Версія для копіювання"
            name="selectedVersion"
            rules={[{ required: true, message: 'Оберіть версію для копіювання' }]}
          >
            <Select placeholder="Оберіть версію">
              {copyVersions.map((version: any) => (
                <Select.Option key={version.id} value={version.id}>
                  Версія {version.version} - {version.name}
                  {version.isActive && <Tag color="green" style={{ marginLeft: 8 }}>Активна</Tag>}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => setIsCopyModalVisible(false)}>
                Скасувати
              </Button>
              <Button type="primary" htmlType="submit">
                Копіювати шаблон
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};